import React, { useState, useEffect, useRef } from 'react';
import { POKEMON_TYPES, TYPE_EFFECTIVENESS } from '../utils/pokemonTypes';
import './TypeEffectivenessQuiz.css';

function TypeEffectivenessQuiz({ isOpen, onClose }) {
  const [moveType, setMoveType] = useState('');
  const [defenderTypes, setDefenderTypes] = useState(['']);
  const [selectedMultiplier, setSelectedMultiplier] = useState(null);
  const [isCorrect, setIsCorrect] = useState(null);
  const [score, setScore] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const chartRowRef = useRef(null);

  const generateNewQuestion = () => {
    // Randomly select move type
    const randomMoveType = POKEMON_TYPES[Math.floor(Math.random() * POKEMON_TYPES.length)];
    
    // Randomly decide if defender has one or two types
    const hasSecondType = Math.random() > 0.5;
    
    // Randomly select first defender type
    let firstDefenderType = POKEMON_TYPES[Math.floor(Math.random() * POKEMON_TYPES.length)];
    
    // Randomly select second defender type (different from first)
    let secondDefenderType = '';
    if (hasSecondType) {
      do {
        secondDefenderType = POKEMON_TYPES[Math.floor(Math.random() * POKEMON_TYPES.length)];
      } while (secondDefenderType === firstDefenderType);
    }

    setMoveType(randomMoveType);
    setDefenderTypes(hasSecondType ? [firstDefenderType, secondDefenderType] : [firstDefenderType]);
    setSelectedMultiplier(null);
    setIsCorrect(null);
  };

  const calculateCorrectMultiplier = () => {
    let multiplier = 1;
    defenderTypes.forEach(defenderType => {
      if (TYPE_EFFECTIVENESS[moveType]?.[defenderType] !== undefined) {
        multiplier *= TYPE_EFFECTIVENESS[moveType][defenderType];
      }
    });
    return multiplier;
  };

  const handleMultiplierSelect = (multiplier) => {
    const correctMultiplier = calculateCorrectMultiplier();
    const correct = multiplier === correctMultiplier;
    
    setSelectedMultiplier(multiplier);
    setIsCorrect(correct);
    setScore(prev => correct ? prev + 1 : prev);
    setTotalQuestions(prev => prev + 1);

    // Scroll to show defender types after a short delay to allow for rendering
    setTimeout(() => {
      if (chartRowRef.current) {
        const row = chartRowRef.current;
        const firstDefenderIndex = POKEMON_TYPES.findIndex(type => defenderTypes.includes(type));
        if (firstDefenderIndex !== -1) {
          const cellWidth = row.children[1].offsetWidth; // Get width of a type cell
          const scrollPosition = cellWidth * (firstDefenderIndex - 2); // Show a couple cells before
          row.scrollTo({ left: scrollPosition, behavior: 'smooth' });
        }
      }
    }, 100);
  };

  useEffect(() => {
    if (isOpen) {
      generateNewQuestion();
      setScore(0);
      setTotalQuestions(0);
    }
  }, [isOpen]);

  const getEffectivenessClass = (value) => {
    if (value === 2) return 'super-effective';
    if (value === 0.5) return 'not-very-effective';
    if (value === 0) return 'immune';
    return 'normal';
  };

  if (!isOpen) return null;

  return (
    <div className="quiz-modal-overlay" onClick={(e) => {
      if (e.target === e.currentTarget) onClose();
    }}>
      <div className="type-quiz-container">
        <div className="quiz-header">
          <h3>Type Effectiveness Quiz</h3>
          <div className="quiz-controls">
            <div className="score">Score: {score}/{totalQuestions}</div>
            {selectedMultiplier !== null && (
              <button className="next-question-arrow" onClick={generateNewQuestion}>
                <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                  <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/>
                </svg>
              </button>
            )}
            <button className="close-button" onClick={onClose}>×</button>
          </div>
        </div>

        <div className="quiz-content">
          <div className="type-display">
            <div className={`move-type type-${moveType.toLowerCase()}`}>
              {moveType} Move
            </div>
            <div className="vs">VS</div>
            <div className="defender-types">
              {defenderTypes.map((type, index) => (
                type && (
                  <div key={index} className={`defender-type type-${type.toLowerCase()}`}>
                    {type}
                  </div>
                )
              ))}
            </div>
          </div>

          <div className="multiplier-options">
            <p>Select the correct damage multiplier:</p>
            <div className="multiplier-buttons">
              {[0, 0.25, 0.5, 1, 2, 4].map((multiplier) => (
                <button
                  key={multiplier}
                  className={`multiplier-button ${getEffectivenessClass(multiplier)} ${
                    selectedMultiplier !== null ? 
                      (multiplier === calculateCorrectMultiplier() ? 'correct' : 
                       multiplier === selectedMultiplier ? 'incorrect' : '') : ''
                  }`}
                  onClick={() => handleMultiplierSelect(multiplier)}
                  disabled={selectedMultiplier !== null}
                >
                  ×{multiplier}
                </button>
              ))}
            </div>
          </div>

          {selectedMultiplier !== null && (
            <div className="quiz-effectiveness-display">
              <div className={`quiz-move-type type-${moveType.toLowerCase()}`}>
                {moveType}
              </div>
              <div className="quiz-defender-cells">
                {defenderTypes.map((type, index) => (
                  type && (
                    <div key={index} className="quiz-effectiveness-cell">
                      <div className={`quiz-type-cell type-${type.toLowerCase()}`}>
                        {type}
                      </div>
                      <div className={`quiz-effectiveness-value ${getEffectivenessClass(TYPE_EFFECTIVENESS[moveType]?.[type])}`}>
                        ×{TYPE_EFFECTIVENESS[moveType]?.[type] ?? 1}
                      </div>
                    </div>
                  )
                ))}
                {defenderTypes.filter(t => t).length > 1 && (
                  <div className="quiz-effectiveness-cell final">
                    <div className="quiz-type-cell final">
                      Final
                    </div>
                    <div className={`quiz-effectiveness-value final ${getEffectivenessClass(calculateCorrectMultiplier())}`}>
                      ×{calculateCorrectMultiplier()}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default TypeEffectivenessQuiz; 